import { User, Log } from "oidc-client-ts";
import { useCallback, useEffect, useState, useRef } from "react";
import SideBar from "./components/SideBar";
import PegaEmbed from "./pega/components/PegaEmbed";
import idleLogoutTimer from "./auth/IdleLogoutTimer";
import { useEnv } from "./context/EnvironmentContext";
import { useUserManager } from "./context/UserManagerContext";
import Logout from "./auth/Logout";
import ProcessCallbacks from "./auth/ProcessCallbacks";
import { useType } from "./context/TypeContext";
import "./styling/App.css";

Log.setLogger(console);
Log.setLevel(Log.DEBUG);

type Config = {
  staticContentServer: string;
};

function App() {
  const [user, setUser] = useState<User | null>(null);
  const [username, setUsername] = useState<string>("Profile");
  const [expandedSidebar, setExpandedSidebar] = useState<boolean>(false);

  const { env } = useEnv();
  const { userManager } = useUserManager();
  const [isCheckingAuth, setIsCheckingAuth] = useState<boolean>(true);
  const { handleManualLogout, handleAutomaticLogout } = Logout(setUser);
  // const { checkTokenExpiry, handleTokenExpired } = HandleTokens(setUser);
  const { processCallback, processSilentCallback } = ProcessCallbacks();
  const [staticContentServer, setStaticContentServer] = useState<string | null>(null);
  const [configLoaded, setConfigLoaded] = useState<boolean>(false);

  const [defaultPortal, setDefaultPortal] = useState<string | null>(null);
  const { setType, setVariant } = useType();

  const toggleSidebar = () => {
    setExpandedSidebar(!expandedSidebar);
  };

  const checkandSetUserName = async () => {
    const currentUser = await userManager.getUser();
    if (currentUser) {
      let username: string = "" + currentUser.profile["full_name"];
      if (!username && currentUser.profile.given_name && currentUser.profile.family_name)
        username = currentUser.profile.given_name + " " + currentUser.profile.family_name;
      username && setUsername(username);
    }
  };

  useEffect(() => {
    if (configLoaded) return;
    if (!user) return;

    const fetchConfig = async () => {
      try {
        const response = await fetch(`${env.BACKEND_URL}/PRRestService/c11nsvc/v1/config`, {
          headers: {
            'Authorization': `Bearer ${user?.access_token}`,
            'Content-Type': 'application/json'
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch script: ${response.statusText}`);
        }

        const config: Config | null = await response.json();

        if (config && config.staticContentServer) {
          fetchAndLoadScript(config.staticContentServer);
          setStaticContentServer(config.staticContentServer);
        }
      } catch (err: any) {
        throw new Error(err.message);
      }
    };

    const fetchBootstrapConfig = async () => {
      try {
        const response = await fetch(`${env.BACKEND_URL}/app/${env.PEGA_APP}/api/application/v2/data_views/D_pxBootstrapConfig`, {
          headers: {
            'Authorization': `Bearer ${user?.access_token}`,
            'Content-Type': 'application/json'
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to fetch script: ${response.statusText}`);
        }

        const bootstrapConfig = await response.json();

        if (bootstrapConfig && bootstrapConfig.pyConfigJSON) {
          const pyConfigJSON = JSON.parse(bootstrapConfig.pyConfigJSON)
          if (pyConfigJSON) {
            const pyOperatorDefaultPortal = pyConfigJSON.environmentInfo?.pxOperator?.pyDefaultPortal;

            if (pyOperatorDefaultPortal) {
              setType("open");

              let variant = "CandidateHomePage"

              if (pyOperatorDefaultPortal.includes("Agent")) {
                variant = "AgentHomePage";
              }
              setVariant(variant);
              setDefaultPortal(pyOperatorDefaultPortal);
            }
          }
        }
      } catch (err: any) {
        throw new Error(err.message);
      }
    };

    user?.access_token && fetchBootstrapConfig();
    user?.access_token && fetchConfig();

    const fetchAndLoadScript = async (staticContentServer: string) => {
      const script = document.createElement('script');
      script.src = `${staticContentServer}pega-embed.js`;
      script.async = true;

      script.onload = () => {
        setConfigLoaded(true);
      };

      document.body.appendChild(script);
      script.onerror = (e) => {
        console.error(`Failed to load script from ${staticContentServer}`, e);
      };

      return () => {
        document.body.removeChild(script);
      };
    };
  }, [user]);

  const checkUser = async () => {
    const currentUser = await userManager.getUser();

    if (!currentUser) return userManager.signinRedirect();

    if (currentUser.expired) {
      return userManager.signinRedirect();
    }

    currentUser.expires_at = currentUser.expires_at || currentUser.profile.exp;
    setUser(currentUser);
    checkandSetUserName();
    await userManager.storeUser(currentUser);
    setIsCheckingAuth(false);
  };

  const userRef = useRef(user)

  useEffect(() => {
    userRef.current = user;
  }, [user]);

  // const handleLogoutCallback = useCallback(() => { if (user) handleAutomaticLogout(user); }, [user, userManager, setUser]);
  const handleLogoutCallback = useCallback(() => {
    if (userRef.current) {
      handleAutomaticLogout(userRef.current)
    }
  }, [userManager, setUser]);

  idleLogoutTimer(handleLogoutCallback, env.INACTIVITY_TIMEOUT_MS, user);

  const updateEmbedToken = (token: string) => {
    const embedElement: any = document.getElementById("theEmbed");
    if (embedElement) {
      embedElement.authHeader = `Bearer ${token}`;
    }
  }

  useEffect(() => {
    const REFRESH_INTERVAL_MS = 4 * 60 * 1000;

    const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

    const attemptTokenRefresh = async (maxRetries = 3) => {
      for (let attempt = 1; attempt <= maxRetries; attempt++) {
        try {
          console.debug(`Forcing token renewal (Attempt ${attempt} of ${maxRetries})...`);
          await userManager.signinSilent();
          return;

        } catch (error) {
          console.warn(`Silent renew failed on attempt ${attempt}.`, error);

          if (attempt < maxRetries) {
            const waitTime = attempt * 2000;
            console.debug(`Waiting ${waitTime}ms before retrying...`);
            await delay(waitTime);
          } else {
            console.error("All refresh attempts failed. Wiping session.");
            await userManager.removeUser();
            setUser(null);
          }
        }
      }
    };

    const manualTimer = setInterval(() => {
      attemptTokenRefresh();
    }, REFRESH_INTERVAL_MS);

    const onUserChange = (newUser: User) => {
      console.debug("onUserChange triggered.");
      updateEmbedToken(newUser.access_token);
      setUser(newUser);
    };

    const onSilentRenewError = (error: Error) => {
      console.debug("Global onSilentRenewError fired:", error.message);
    };

    const onUserUnloaded = () => {
      console.debug("onUserUnloaded triggered.");
      setUser(null);
    };

    userManager.events.addUserLoaded(onUserChange);
    userManager.events.addSilentRenewError(onSilentRenewError);
    userManager.events.addUserUnloaded(onUserUnloaded);

    const urlParams = new URLSearchParams(window.location.search);

    console.debug("urlParams:", urlParams);
    if (urlParams.has("error")) {
      console.error("Invalid response from FIAM; No user found in the callback response");
      setIsCheckingAuth(false);
    } else if (urlParams.has("code")) {
      processCallback();
    } else if (urlParams.has("silent")) {
      processSilentCallback();
    } else {
      checkUser().then(console.log);
    }

    return () => {
      userManager.events.removeUserLoaded(onUserChange);
      userManager.events.removeUserUnloaded(onUserUnloaded);
      userManager.events.removeSilentRenewError(onSilentRenewError);
      clearInterval(manualTimer);
    };
  }, []);

  const isAppLoading = isCheckingAuth || (user?.access_token && (!staticContentServer || !defaultPortal));

  if (isAppLoading) {
    return (
      <div className="d-flex justify-content-center">
        <div className="spinner-border m-5 spinner-custom-brand" role="status">
          <span className="visually-hidden">Loading Application ...</span>
        </div>
      </div>
    );
  }
  return (
    <>
      {user?.access_token && defaultPortal && <SideBar
        expanded={expandedSidebar}
        username={username}
        handleLogout={handleManualLogout}
        toggleSidebar={toggleSidebar}
        defaultPortal={defaultPortal}
      />}

      <div
        className="content"
        style={{
          // marginLeft: expandedSidebar ? "200px" : "60px",
          marginLeft: expandedSidebar ? "15.5rem" : "4rem",
          transition: "margin-left 0.1s",
        }}
      >
        {
          user?.access_token && staticContentServer && defaultPortal ? (<PegaEmbed accessToken={user.access_token} staticContentServer={staticContentServer} />) : ("Not signed in.")
        }
      </div>
    </>
  );
}

export default App;
